import React from 'react';
import { Search, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface ShopFiltersProps {
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export default function ShopFilters({ 
  selectedCategory, 
  onCategoryChange,
  searchQuery,
  onSearchChange
}: ShopFiltersProps) {
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchCategories() {
      try {
        const { data } = await supabase
          .from('product_categories')
          .select('id, name, slug')
          .order('name');
        
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, []);

  const handleClearSearch = () => {
    onSearchChange('');
  };

  const handleClearCategory = () => {
    onCategoryChange(null);
  };

  const handleClearAll = () => {
    onSearchChange('');
    onCategoryChange(null);
  };

  const hasActiveFilters = searchQuery || selectedCategory;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Category Filters */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900">Categories</h3>
          {selectedCategory && (
            <button
              onClick={handleClearCategory}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>
        <div className="space-y-2">
          {categories.map(category => (
            <label
              key={category.id}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <input
                type="radio"
                name="category"
                checked={selectedCategory === category.slug}
                onChange={() => onCategoryChange(
                  selectedCategory === category.slug ? null : category.slug
                )}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span className="text-gray-700 group-hover:text-primary-600">
                {category.name}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Clear All Filters */}
      {hasActiveFilters && (
        <div className="mt-6 pt-6 border-t">
          <button
            onClick={handleClearAll}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4" />
            <span>Clear All Filters</span>
          </button>
        </div>
      )}
    </div>
  );
}